import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Coins, Gift, QrCode as QrIcon } from "lucide-react";
import { useMember } from "@/contexts/MemberContext";
import { BRAND } from "@/constants/brand";

export default function MemberAuth() {
  const { member, loading, login, signup } = useMember();
  const location = useLocation();
  const [mode, setMode] = useState(() => (location.pathname.endsWith("/login") ? "login" : "signup"));
  const [form, setForm] = useState({
    name: "", email: "", phone: "", password: "", city: "",
  });
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!loading && member) nav("/member/dashboard", { replace: true });
  }, [member, loading, nav]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = mode === "signup"
      ? await signup(form)
      : await login(form.email, form.password);
    setBusy(false);
    if (r.ok) {
      toast.success(mode === "signup" ? "Welcome to Pop Panda!" : "Welcome back!");
      nav("/member/dashboard", { replace: true });
    } else {
      toast.error(r.error || "Something went wrong");
    }
  };

  return (
    <div data-testid="member-auth-page" className="min-h-screen bg-gradient-to-br from-[#2B2B2B] via-[#3a2f1a] to-[#2B2B2B] relative overflow-hidden">
      {/* floating background orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div className="absolute -top-40 -left-20 w-[520px] h-[520px] rounded-full bg-brand-yellow/25 blur-3xl" animate={{ x: [0, 40, 0], y: [0, 30, 0] }} transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }} />
        <motion.div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-[#FF4F5A]/20 blur-3xl" animate={{ x: [0, -30, 0], y: [0, -20, 0] }} transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }} />
      </div>

      <div className="relative z-10 min-h-screen grid lg:grid-cols-2">
        {/* LEFT: Brand */}
        <div className="hidden lg:flex flex-col justify-between p-12 text-brand-cream">
          <Link to="/" className="flex items-center gap-3" data-testid="member-auth-home">
            <img src={BRAND.logoUrl} alt="Pop Panda" className="h-10 w-10 bg-brand-cream rounded-full p-1.5" />
            <div className="font-display text-2xl">Pop Panda</div>
          </Link>

          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-brand-yellow">Membership & Rewards</div>
            <h1 className="font-display text-5xl xl:text-6xl mt-4 leading-[1.02]">Earn Pop Coins.<br /><span className="text-brand-yellow">Live the Panda life.</span></h1>
            <p className="mt-6 text-brand-cream/70 max-w-md text-base leading-relaxed">
              Join The Pop Panda Club — a members-only world of free popsicles, VIP tastings, exclusive drops and glowing surprises with every scoop.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {[
                { icon: Coins, label: "Earn coins on every purchase" },
                { icon: Gift, label: "Redeem for free treats" },
                { icon: QrIcon, label: "Your own QR pass" },
              ].map((f, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.1 }} className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-xl p-4">
                  <f.icon size={22} className="text-brand-yellow" />
                  <div className="mt-2 text-[11px] leading-snug text-brand-cream/80">{f.label}</div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="text-xs text-brand-cream/40">© {new Date().getFullYear()} The Pop Panda Club</div>
        </div>

        {/* RIGHT: Auth card */}
        <div className="grid place-items-center p-6 lg:p-10">
          <motion.form
            key={mode}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            onSubmit={onSubmit}
            data-testid={mode === "signup" ? "member-signup-form" : "member-login-form"}
            className="w-full max-w-md rounded-3xl bg-white/10 border border-white/15 backdrop-blur-2xl p-8 shadow-2xl text-brand-cream space-y-5"
          >
            <div className="flex items-center gap-2 text-brand-yellow">
              <Sparkles size={16} /><div className="text-[11px] uppercase tracking-[0.24em]">The Pop Panda Club</div>
            </div>
            <h2 className="font-display text-4xl leading-none">
              {mode === "signup" ? "Become a member" : "Welcome back"}
            </h2>

            <AnimatePresence mode="wait">
              {mode === "signup" && (
                <motion.div key="signup-fields" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="space-y-4 overflow-hidden">
                  <Field label="Full name" testid="member-name">
                    <input required value={form.name} onChange={update("name")} data-testid="member-name-input" className="pp-glass-input" placeholder="e.g. Arjun Rao" />
                  </Field>
                  <Field label="Phone number">
                    <input required value={form.phone} onChange={update("phone")} data-testid="member-phone-input" className="pp-glass-input" placeholder="10-digit mobile" />
                  </Field>
                  <Field label="City (optional)">
                    <input value={form.city} onChange={update("city")} data-testid="member-city-input" className="pp-glass-input" placeholder="e.g. Bengaluru" />
                  </Field>
                </motion.div>
              )}
            </AnimatePresence>

            <Field label="Email">
              <input required type="email" value={form.email} onChange={update("email")} data-testid="member-email-input" className="pp-glass-input" placeholder="you@example.com" />
            </Field>
            <Field label="Password">
              <input required type="password" value={form.password} onChange={update("password")} data-testid="member-password-input" className="pp-glass-input" placeholder={mode === "signup" ? "Create a strong password" : "Enter your password"} />
            </Field>

            <button disabled={busy} data-testid="member-auth-submit" className="w-full rounded-full bg-brand-yellow text-brand-charcoal font-medium py-3 hover:opacity-95 transition disabled:opacity-50">
              {busy ? "Please wait…" : (mode === "signup" ? "Join the Club — Get 50 Pop Coins" : "Sign in")}
            </button>

            <div className="text-center text-sm text-brand-cream/70">
              {mode === "signup" ? (
                <>Already a member? <button type="button" onClick={() => setMode("login")} data-testid="switch-to-login" className="text-brand-yellow underline">Sign in</button></>
              ) : (
                <>New here? <button type="button" onClick={() => setMode("signup")} data-testid="switch-to-signup" className="text-brand-yellow underline">Create an account</button></>
              )}
            </div>

            <div className="text-center">
              <Link to="/" data-testid="member-back-home" className="text-xs text-brand-cream/50 hover:text-brand-cream">← Back to site</Link>
            </div>
          </motion.form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-[0.2em] text-brand-cream/60 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
